/* eslint-disable max-statements */

import { Button, Group, ScrollArea, Stack, Title } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { modals } from '@mantine/modals';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { Customer, MultilingualCodeSystems } from '@e-origin/shared';

import { IconFactory } from '@assets';
import { LeavePageNotification, StickyContainer, Table, TableActionEnum, TableActions, TableColumnProps } from '@components';
import { EditCustomerType } from '@enums';
import { useDeclarant } from '@hooks/declarant';
import { retrieveCodes } from '@hooks/retrieveCodes';
import { EditCustomerViewTransitAuth } from './EditCustomerViewTransitAuth';
import { useCustomerViewTransitAuthFormInitializer } from './useCustomerViewTransitAuthFormInitializer';

interface CustomerViewTransitAuthProps {
  handleSubmitCustomer(type: EditCustomerType, childForm: UseFormReturnType<any>): void;
  customerDetails: Customer;
  isCreate: boolean;
}

export const CustomerViewTransitAuth: React.FC<CustomerViewTransitAuthProps> = ({ customerDetails, handleSubmitCustomer, isCreate }) => {
  const selectedItemInfo = useRef<{ ref: { type: string; referenceNumber: string }; idx: number }>({
    ref: null,
    idx: null,
  });
  const [authorisationTypes, setAuthorisationTypes] = useState<{ value?: string; label?: string }[]>([]);

  const { form, buildFormValues } = useCustomerViewTransitAuthFormInitializer({ customerDetails });

  const { data: declarant } = useDeclarant();

  useEffect(() => {
    retrieveCodes(['AuthorisationTypeDeparture', 'AuthorisationTypeDestination'], '', declarant?.language, [MultilingualCodeSystems.NCTSP5]).then(
      (response) => {
        setAuthorisationTypes(response);
      },
    );
  }, [declarant?.language]);

  useEffect(() => {
    if (customerDetails) {
      form.setValues(buildFormValues(customerDetails));
      form.resetDirty({} as any);
    }
  }, [customerDetails]);

  const handleTableAction = (actionType: string, row: { type: string; referenceNumber: string; limitDateDays?: number }, rowIndex: number) => {
    if (actionType === TableActionEnum.Edit) {
      selectedItemInfo.current = { ref: row, idx: rowIndex };
      openTransitAuthModal(row);
    }

    if (actionType === TableActionEnum.Delete) {
      const transitAuthorisations = [...form.values.transitAuthorisations];
      const index = transitAuthorisations.findIndex((t) => t.type === row.type);
      transitAuthorisations.splice(index, 1);

      form.setFieldValue('transitAuthorisations', transitAuthorisations);
    }
  };

  const handleSavingTransitAuth = (updatedTransitAuthReference: { type: string; referenceNumber: string; limitDateDays?: number }) => {
    const { ref, idx } = selectedItemInfo.current;
    const transitAuthorisations = [...form.values.transitAuthorisations];
    const alreadyExists = transitAuthorisations.find((t) => t.type === updatedTransitAuthReference.type);
    const index = transitAuthorisations.findIndex((t) => t.type === updatedTransitAuthReference.type);

    if (!!alreadyExists && idx >= 0 && idx !== index) {
      toast.error('This authorisation type already exists for this customer!');
      return;
    }

    if (alreadyExists) {
      transitAuthorisations.splice(index, 1, updatedTransitAuthReference);
    } else if (ref) {
      transitAuthorisations.splice(idx, 1, updatedTransitAuthReference);
    } else {
      transitAuthorisations.push(updatedTransitAuthReference);
    }

    form.setFieldValue('transitAuthorisations', transitAuthorisations);
    selectedItemInfo.current = { ref: null, idx: null };
  };

  const typeValue = (t) => {
    return authorisationTypes.find((type) => {
      return type.value === t;
    });
  };

  const columns: TableColumnProps<any>[] = [
    {
      name: 'Description',
      cell: (row) => <div title={typeValue(row.type)?.label}>{typeValue(row.type)?.label}</div>,
      width: 500,
    },
    {
      name: 'Code',
      cell: (row) => row.type,
    },
    {
      name: 'Reference number',
      cell: (row) => row.referenceNumber,
    },
    {
      name: 'Default limit date (days)',
      cell: (row) => row.limitDateDays,
    },
    {
      name: 'Actions',
      sticky: 'right',
      cell: (row: { type: string; referenceNumber: string; limitDateDays?: number | null }, rowIndex: number) => (
        <TableActions
          actions={[
            {
              icon: IconFactory.view,
              text: 'View details',
              onClick: () => handleTableAction(TableActionEnum.Edit, row, rowIndex),
            },
            {
              icon: IconFactory.delete,
              text: 'Delete',
              onClick: () => handleTableAction(TableActionEnum.Delete, row, rowIndex),
            },
          ]}
        />
      ),
    },
  ];

  const openTransitAuthModal = (transitAuth?: { type: string; referenceNumber: string; limitDateDays?: number }) => {
    modals.open({
      title: transitAuth ? 'Edit transit authorization' : 'Create transit authorization',
      children: <EditCustomerViewTransitAuth transitAuthEdit={transitAuth} onHide={modals.closeAll} onSave={handleSavingTransitAuth} />,
    });
  };

  return (
    <>
      <LeavePageNotification hasUnsavedChanges={form.isDirty()} />

      <ScrollArea h="100%" offsetScrollbars="y">
        <Stack mx="xl">
          <Group justify="space-between" my="xl">
            <Title order={5} fw={600}>
              Authorisations for transit procedure
            </Title>
            <Button onClick={() => openTransitAuthModal()} type="submit" variant="filled" size="lg" mb="xl">
              Add
            </Button>
          </Group>

          <Table columns={columns} dataSource={form.values.transitAuthorisations} pagination verticalSpacing="xs" />
        </Stack>

        <StickyContainer>
          <Button onClick={() => handleSubmitCustomer(EditCustomerType.TRANSIT_AUTH, form)} type="submit" variant="filled" size="lg">
            {isCreate ? 'Create' : 'Save'}
          </Button>
        </StickyContainer>
      </ScrollArea>
    </>
  );
};
